/* sb-dc-proofcap.js — build the proof picture at SCREEN size, not print size.
   2026-09-06.

   The Review proof window asks the bundle for its preview picture like this:

       render('proof', 96, 'proof')

   96 dpi is written into the bundle as a constant. On a 5x3 inch sticker that
   is 480x288 and exactly right. On an 8'x8' step & repeat it is 9216x9216,
   which the browser's own canvas ceiling then trims to 8192x8192 — sixty-seven
   million pixels. Measured on the live site, 2026-09-06:

       Step & Repeat 8'x8'    8192 x 8192   67.1 MP   295 ms   1.70 MB
       Vinyl banner  8'x4'    9216 x 4608   42.5 MP   206 ms   1.06 MB
       Pop-up banner 31.5x80" 3024 x 7680   23.2 MP   135 ms   0.60 MB
       Oval sticker  5"x3"      480 x  288    0.1 MP    23 ms   0.02 MB

   And then it is shown at, at most, 280 css pixels tall:

       .proof-stage img{max-width:100%;max-height:280px}   (sb-dc-app.css)

   Measured on screen: 280x280 for the step & repeat, 560x280 for the banner,
   110x280 for the pop-up. So the square one builds 856 times the pixels it
   displays, and there is nowhere for the rest to go — the bundle mentions
   proof-stage exactly once, there is no click-to-enlarge, and no download.

   8192x8192x4 bytes is 268 MB of raw canvas, and the browser decodes the data
   URL back into another bitmap of the same size to paint the <img>. That is
   the memory a phone does not have. (That figure is arithmetic, not a
   measurement — browsers do not report canvas memory to script.)

   Why it is safe to shrink. The proof picture is not the product. Traced
   through the bundle: the object render('proof',...) returns is read in
   exactly three places, all in one expression — the null check, and .dataUrl
   for the <img src>. Its .blob is never touched. The print file has its own
   separate path, render('print', __dcPrintDpi(spec), name), which sb-dc-aug.js
   calls when it uploads and when the customer downloads. Every number and
   every sentence in the proof window ("114 dpi", "Browser canvas limit
   means...") is computed from plan('print', ...), never from the proof. So
   lowering the proof dpi cannot change one word the customer reads, cannot
   change the file the press receives, and cannot change the order.

   The rule: give the screen 2000 pixels on the longest side. The picture is
   never displayed wider than the modal (560 css px measured), so 2000 is about
   3.5x what the densest phone screen can resolve, with room for browser zoom.

   min() only. The dpi is never raised above what the bundle asked for, so
   small products are untouched — the oval sticker still comes out 480x288,
   pixel for pixel.

   Where this sits. window.__dcExportRef is the bundle's own export handle;
   sb-dc-aug.js already calls .current.render() and .current.plan() through it.
   The bundle replaces .current whenever the board changes, so a one-time wrap
   would be thrown away on the next size change. Instead this defines an
   accessor on the ref object itself — that object is a useRef and keeps its
   identity for the life of the editor — and re-wraps whatever gets assigned.

   ⚠ Only 'proof' is touched. 'print' is passed through untouched, argument for
   argument, and so is plan(). If the wrap throws for any reason the original
   call is made instead, so this file can never stop an export.

   ponytail: one constant. If a click-to-enlarge is ever added to the proof,
   raise CAP to what the enlarged view needs.

   Diagnostics: window.__dcProofCap — .installed, .calls, .capped, .last, and
   .test() which returns the names of any failing self-check. */
(function () {
  'use strict';

  var CAP = 2000;   // longest side of the proof picture, in pixels

  var log = { installed: false, calls: 0, capped: 0, last: null };

  /* The dpi that puts CAP pixels on the longest side. Never raises, and hands
     back the original value whenever the size is not usable. */
  function lower(dpi, spec) {
    if (!(dpi > 0)) return dpi;
    var w = spec && +spec.widthIn, h = spec && +spec.heightIn;
    var longest = Math.max(w > 0 ? w : 0, h > 0 ? h : 0);
    if (!longest) return dpi;
    return Math.min(dpi, CAP / longest);
  }

  function wrap(obj) {
    if (!obj || typeof obj.render !== 'function' || obj.render.__dcProofCapped) return obj;
    var real = obj.render;

    var capped = function (mode, dpi, name) {
      if (mode !== 'proof') return real.apply(this, arguments);   // 인쇄는 손대지 않는다
      try {
        var spec = (window.__dcApi && window.__dcApi.spec) || null;
        var out = lower(dpi, spec);
        log.calls++;
        if (out < dpi) {
          log.capped++;
          log.last = { widthIn: spec.widthIn, heightIn: spec.heightIn,
                       from: dpi, to: Math.round(out * 10) / 10 };
        }
        return real.call(this, mode, out, name);
      } catch (e) {
        return real.apply(this, arguments);   // 못 줄이겠으면 원래대로 — 절대 막지 않는다
      }
    };
    capped.__dcProofCapped = true;
    obj.render = capped;
    return obj;
  }

  /* patched is the ref object we have already put an accessor on. Comparing
     against it (rather than a plain boolean) means a remount that hands us a
     brand new ref is picked up too. */
  var patched = null;

  function install() {
    var ref = window.__dcExportRef;
    if (!ref || ref === patched) return !!patched;
    var slot = wrap(ref.current);
    try {
      Object.defineProperty(ref, 'current', {
        configurable: true,
        get: function () { return slot; },
        set: function (v) { slot = wrap(v); }
      });
    } catch (e) { return false; }        // 못 걸면 그냥 지금까지 하던 대로 돈다
    patched = ref;
    log.installed = true;
    return true;
  }

  /* Unlike sb-dc-printcap.js this keeps checking for the full 30 seconds after
     a success, because the ref can be replaced by a remount during start-up.
     After that the editor is up and the handle stops moving. */
  var tries = 0;
  var t = setInterval(function () { install(); if (++tries > 150) clearInterval(t); }, 200);
  window.addEventListener('pagehide', function () { clearInterval(t); });

  function test() {
    var bad = [];
    var near = function (a, b) { return Math.abs(a - b) < 1e-9; };
    [['배너 8x4', 96, { widthIn: 96, heightIn: 48 }, CAP / 96],
     ['정사각 8x8', 96, { widthIn: 96, heightIn: 96 }, CAP / 96],
     ['세로로 긴 배너', 96, { widthIn: 31.5, heightIn: 80 }, CAP / 80],
     ['작은 스티커는 그대로', 96, { widthIn: 5, heightIn: 3 }, 96],
     ['딱 맞는 크기는 그대로', 100, { widthIn: 20, heightIn: 10 }, 100],
     ['규격 없음', 96, null, 96],
     ['규격이 0', 96, { widthIn: 0, heightIn: 0 }, 96],
     ['규격이 글자', 96, { widthIn: 'x', heightIn: 'y' }, 96],
     ['dpi 가 0', 0, { widthIn: 96, heightIn: 48 }, 0]
    ].forEach(function (c) { if (!near(lower(c[1], c[2]), c[3])) bad.push(c[0]); });

    /* 인쇄 요청은 한 글자도 바뀌지 않아야 한다 */
    var saw = null;
    var fake = { render: function (m, d, n) { saw = [m, d, n]; return 'ok'; } };
    wrap(fake);
    if (fake.render('print', 114, 'front_print') !== 'ok') bad.push('인쇄 반환값');
    if (!saw || saw[0] !== 'print' || saw[1] !== 114 || saw[2] !== 'front_print') bad.push('인쇄 인자');
    if (wrap(fake).render.__dcProofCapped !== true) bad.push('두 번 감쌈 방지');
    return bad;
  }

  log.test = test;
  window.__dcProofCap = log;

  try {
    var bad = test();
    if (bad.length) console.error('sb-dc-proofcap self-check failed:', bad);
  } catch (e) { console.error('sb-dc-proofcap self-check threw:', e); }
})();
